import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowLeft,
  CaretDown,
  LinkSimple,
  ListBullets,
  ListNumbers,
  Paragraph,
  TextB,
  TextH,
  TextItalic,
  TextUnderline,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { SimpleEditorProps } from '@/lib/types/tiptap.types';
import {
  normalizeEventDescriptionLinkUrl,
  sanitizeEventDescriptionHtml,
} from '@/lib/utils/event-description.utils';
import '@/components/tiptap-templates/simple/simple-editor.scss';

const HEADING_LEVELS = [1, 2, 3] as const;
const MOBILE_BREAKPOINT_QUERY = '(max-width: 640px)';
const MOBILE_TOOLBAR_NAV_OFFSET = 104;
const MOBILE_TOOLBAR_KEYBOARD_GAP = 8;
const MOBILE_CURSOR_BUFFER = 12;
const MOBILE_TOOLBAR_INTERACTION_GRACE_MS = 250;

const HEADING_OPTIONS = [
  {
    label: 'Paragraph',
    shortLabel: 'P',
    isActive: (editor: Editor) => editor.isActive('paragraph'),
    run: (editor: Editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    label: 'Heading 1',
    shortLabel: 'H1',
    isActive: (editor: Editor) => editor.isActive('heading', { level: 1 }),
    run: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    label: 'Heading 2',
    shortLabel: 'H2',
    isActive: (editor: Editor) => editor.isActive('heading', { level: 2 }),
    run: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: 'Heading 3',
    shortLabel: 'H3',
    isActive: (editor: Editor) => editor.isActive('heading', { level: 3 }),
    run: (editor: Editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
] as const;

type MobileToolbarView = 'main' | 'link';

type ToolbarButtonProps = ComponentProps<typeof Button> & {
  active?: boolean;
  preserveFocus?: boolean;
};

type LinkEditorProps = {
  href: string;
  onHrefChange: (value: string) => void;
  onApply: () => void;
  onRemove: () => void;
  onOpenLink: () => void;
  canRemove: boolean;
};

type MainToolbarProps = {
  editor: Editor;
  isMobile?: boolean;
  onOpenLink: () => void;
  onOverlayOpenChange?: (isOpen: boolean) => void;
  onToolbarInteraction?: () => void;
  linkEditor?: ReactNode;
};

function preventToolbarBlur(event: MouseEvent<HTMLElement>) {
  event.preventDefault();
}

function getViewportMetrics() {
  if (typeof window === 'undefined') {
    return { height: 0, offsetTop: 0 };
  }

  const viewport = window.visualViewport;

  return {
    height: viewport?.height ?? window.innerHeight,
    offsetTop: viewport?.offsetTop ?? 0,
  };
}

function useIsMobileToolbar() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return isMobile;
}

function useViewportMetrics() {
  const [metrics, setMetrics] = useState(getViewportMetrics);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const viewport = window.visualViewport;
    const updateMetrics = () => setMetrics(getViewportMetrics());

    updateMetrics();
    window.addEventListener('resize', updateMetrics);
    viewport?.addEventListener('resize', updateMetrics);
    viewport?.addEventListener('scroll', updateMetrics);

    return () => {
      window.removeEventListener('resize', updateMetrics);
      viewport?.removeEventListener('resize', updateMetrics);
      viewport?.removeEventListener('scroll', updateMetrics);
    };
  }, []);

  return metrics;
}

function getSelectedHeadingLabel(editor: Editor) {
  return (
    HEADING_OPTIONS.find((option) => option.isActive(editor))?.shortLabel ?? 'H'
  );
}

const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  (
    { active = false, className, onMouseDown, preserveFocus = true, ...props },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'simple-editor__toolbar-button',
          active && 'simple-editor__toolbar-button--active',
          className
        )}
        onMouseDown={(event) => {
          if (preserveFocus) {
            preventToolbarBlur(event);
          }
          onMouseDown?.(event);
        }}
        {...props}
      />
    );
  }
);
ToolbarButton.displayName = 'ToolbarButton';

function LinkEditor({
  href,
  onHrefChange,
  onApply,
  onRemove,
  onOpenLink,
  canRemove,
}: LinkEditorProps) {
  return (
    <div className="simple-editor__link-editor">
      <Input
        value={href}
        onChange={(event) => onHrefChange(event.target.value)}
        placeholder="Add a link"
        className="simple-editor__link-input"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
      />
      <div className="simple-editor__link-actions">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onOpenLink}
          disabled={!canRemove}
        >
          Open
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRemove}
          disabled={!canRemove}
        >
          Remove
        </Button>
        <Button type="button" size="sm" onClick={onApply}>
          Apply
        </Button>
      </div>
    </div>
  );
}

function MainToolbar({
  editor,
  isMobile = false,
  onOpenLink,
  onOverlayOpenChange,
  onToolbarInteraction,
  linkEditor,
}: MainToolbarProps) {
  const editorState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => ({
      canUndo: currentEditor.can().chain().focus().undo().run(),
      canRedo: currentEditor.can().chain().focus().redo().run(),
      isBold: currentEditor.isActive('bold'),
      canBold: currentEditor.can().chain().focus().toggleBold().run(),
      isItalic: currentEditor.isActive('italic'),
      canItalic: currentEditor.can().chain().focus().toggleItalic().run(),
      isUnderline: currentEditor.isActive('underline'),
      canUnderline: currentEditor.can().chain().focus().toggleUnderline().run(),
      isBulletList: currentEditor.isActive('bulletList'),
      canBulletList: currentEditor
        .can()
        .chain()
        .focus()
        .toggleBulletList()
        .run(),
      isOrderedList: currentEditor.isActive('orderedList'),
      canOrderedList: currentEditor
        .can()
        .chain()
        .focus()
        .toggleOrderedList()
        .run(),
      isLink: currentEditor.isActive('link'),
      headingLabel: getSelectedHeadingLabel(currentEditor),
    }),
  });
  const [isHeadingMenuOpen, setIsHeadingMenuOpen] = useState(false);
  const [isListMenuOpen, setIsListMenuOpen] = useState(false);
  const [isDesktopLinkOpen, setIsDesktopLinkOpen] = useState(false);

  useEffect(() => {
    onOverlayOpenChange?.(
      isHeadingMenuOpen || isListMenuOpen || isDesktopLinkOpen
    );
  }, [
    isDesktopLinkOpen,
    isHeadingMenuOpen,
    isListMenuOpen,
    onOverlayOpenChange,
  ]);

  useEffect(() => {
    return () => {
      onOverlayOpenChange?.(false);
    };
  }, [onOverlayOpenChange]);

  return (
    <>
      <div className="simple-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editorState.canUndo}
          aria-label="Undo"
        >
          <ArrowCounterClockwise size={18} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editorState.canRedo}
          aria-label="Redo"
        >
          <ArrowClockwise size={18} weight="bold" />
        </ToolbarButton>
      </div>

      <div className="simple-editor__toolbar-separator" />

      <div className="simple-editor__toolbar-group">
        <Popover open={isHeadingMenuOpen} onOpenChange={setIsHeadingMenuOpen}>
          <PopoverTrigger asChild>
            <ToolbarButton
              className="simple-editor__toolbar-button--menu"
              aria-label="Heading options"
              preserveFocus={false}
            >
              <TextH size={18} weight="bold" />
              <span className="simple-editor__toolbar-label">
                {editorState.headingLabel}
              </span>
              <CaretDown size={14} weight="bold" />
            </ToolbarButton>
          </PopoverTrigger>
          <PopoverContent
            side={isMobile ? 'top' : 'bottom'}
            align="start"
            sideOffset={8}
            collisionPadding={16}
            className={cn('simple-editor__menu', isMobile && 'z-[80]')}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onPointerDownCapture={onToolbarInteraction}
          >
            {HEADING_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                className={cn(
                  'simple-editor__menu-item',
                  option.isActive(editor) && 'simple-editor__menu-item--active'
                )}
                onMouseDown={preventToolbarBlur}
                onClick={() => {
                  option.run(editor);
                  setIsHeadingMenuOpen(false);
                }}
              >
                <span>{option.label}</span>
                <span className="simple-editor__menu-item-short">
                  {option.shortLabel}
                </span>
              </button>
            ))}
          </PopoverContent>
        </Popover>

        <Popover open={isListMenuOpen} onOpenChange={setIsListMenuOpen}>
          <PopoverTrigger asChild>
            <ToolbarButton
              className="simple-editor__toolbar-button--menu"
              aria-label="List options"
              preserveFocus={false}
            >
              {editorState.isOrderedList ? (
                <ListNumbers size={18} weight="bold" />
              ) : (
                <ListBullets size={18} weight="bold" />
              )}
              <CaretDown size={14} weight="bold" />
            </ToolbarButton>
          </PopoverTrigger>
          <PopoverContent
            side={isMobile ? 'top' : 'bottom'}
            align="start"
            sideOffset={8}
            collisionPadding={16}
            className={cn('simple-editor__menu', isMobile && 'z-[80]')}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onPointerDownCapture={onToolbarInteraction}
          >
            <button
              type="button"
              className={cn(
                'simple-editor__menu-item',
                editorState.isBulletList && 'simple-editor__menu-item--active'
              )}
              onMouseDown={preventToolbarBlur}
              onClick={() => {
                editor.chain().focus().toggleBulletList().run();
                setIsListMenuOpen(false);
              }}
              disabled={!editorState.canBulletList}
            >
              <span>Bulleted list</span>
              <ListBullets size={18} weight="bold" />
            </button>
            <button
              type="button"
              className={cn(
                'simple-editor__menu-item',
                editorState.isOrderedList && 'simple-editor__menu-item--active'
              )}
              onMouseDown={preventToolbarBlur}
              onClick={() => {
                editor.chain().focus().toggleOrderedList().run();
                setIsListMenuOpen(false);
              }}
              disabled={!editorState.canOrderedList}
            >
              <span>Numbered list</span>
              <ListNumbers size={18} weight="bold" />
            </button>
            <button
              type="button"
              className={cn(
                'simple-editor__menu-item',
                editor.isActive('paragraph') &&
                  !editorState.isBulletList &&
                  !editorState.isOrderedList &&
                  'simple-editor__menu-item--active'
              )}
              onMouseDown={preventToolbarBlur}
              onClick={() => {
                editor.chain().focus().liftListItem('listItem').run();
                editor.chain().focus().setParagraph().run();
                setIsListMenuOpen(false);
              }}
            >
              <span>No list</span>
              <Paragraph size={18} weight="bold" />
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <div className="simple-editor__toolbar-separator" />

      <div className="simple-editor__toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
          active={editorState.isBold}
          aria-label="Bold"
          aria-pressed={editorState.isBold}
        >
          <TextB size={18} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
          active={editorState.isItalic}
          aria-label="Italic"
          aria-pressed={editorState.isItalic}
        >
          <TextItalic size={18} weight="bold" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editorState.canUnderline}
          active={editorState.isUnderline}
          aria-label="Underline"
          aria-pressed={editorState.isUnderline}
        >
          <TextUnderline size={18} weight="bold" />
        </ToolbarButton>
        {linkEditor ? (
          <Popover open={isDesktopLinkOpen} onOpenChange={setIsDesktopLinkOpen}>
            <PopoverTrigger asChild>
              <ToolbarButton
                active={editorState.isLink}
                aria-label="Link"
                aria-pressed={editorState.isLink}
                preserveFocus={false}
                onClick={() => {
                  onOpenLink();
                  setIsDesktopLinkOpen(true);
                }}
              >
                <LinkSimple size={18} weight="bold" />
              </ToolbarButton>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={10}
              className="simple-editor__link-popover"
            >
              {linkEditor}
            </PopoverContent>
          </Popover>
        ) : (
          <ToolbarButton
            onClick={onOpenLink}
            active={editorState.isLink}
            aria-label="Link"
            aria-pressed={editorState.isLink}
          >
            <LinkSimple size={18} weight="bold" />
          </ToolbarButton>
        )}
      </div>
    </>
  );
}

export function SimpleEditor({
  value,
  onChange,
  placeholder = 'Start typing…',
  inputId,
  ariaLabel = 'Event description editor',
  disabled = false,
  invalid = false,
  onBlur,
  onFocus,
}: SimpleEditorProps) {
  const isMobile = useIsMobileToolbar();
  const { height: viewportHeight, offsetTop } = useViewportMetrics();
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const toolbarInteractionTimeoutRef = useRef<number | null>(null);
  const isToolbarInteractingRef = useRef(false);
  const isMobilePopoverOpenRef = useRef(false);
  const mobileToolbarViewRef = useRef<MobileToolbarView>('main');
  const [mobileToolbarView, setMobileToolbarView] =
    useState<MobileToolbarView>('main');
  const [isMobileToolbarVisible, setIsMobileToolbarVisible] = useState(false);
  const [isMobilePopoverOpen, setIsMobilePopoverOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('');

  useEffect(() => {
    isMobilePopoverOpenRef.current = isMobilePopoverOpen;
  }, [isMobilePopoverOpen]);

  useEffect(() => {
    mobileToolbarViewRef.current = mobileToolbarView;
  }, [mobileToolbarView]);

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [...HEADING_LEVELS],
        },
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        enableClickSelection: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer nofollow',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        ...(inputId ? { id: inputId } : {}),
        'class': 'simple-editor__content',
        'aria-label': ariaLabel,
        'aria-invalid': invalid ? 'true' : 'false',
        'autocomplete': 'off',
        'autocorrect': 'off',
        'autocapitalize': 'off',
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      const nextValue = sanitizeEventDescriptionHtml(currentEditor.getHTML());

      if (nextValue !== value) {
        onChange(nextValue);
      }
    },
    onFocus: () => {
      if (isMobile) {
        setIsMobileToolbarVisible(true);
      }

      onFocus?.();
    },
    onBlur: ({ editor: currentEditor }) => {
      onBlur?.();

      if (!isMobile) {
        return;
      }

      window.requestAnimationFrame(() => {
        const activeElement = document.activeElement;

        if (toolbarRef.current?.contains(activeElement)) {
          setIsMobileToolbarVisible(true);
          return;
        }

        if (isToolbarInteractingRef.current || isMobilePopoverOpenRef.current) {
          setIsMobileToolbarVisible(true);
          return;
        }

        if (!currentEditor.isFocused) {
          setIsMobileToolbarVisible(false);
          setMobileToolbarView('main');
        }
      });
    },
  });

  useEffect(() => {
    return () => {
      if (toolbarInteractionTimeoutRef.current !== null) {
        window.clearTimeout(toolbarInteractionTimeoutRef.current);
      }
    };
  }, []);

  const registerToolbarInteraction = () => {
    if (!isMobile || typeof window === 'undefined') {
      return;
    }

    isToolbarInteractingRef.current = true;

    if (toolbarInteractionTimeoutRef.current !== null) {
      window.clearTimeout(toolbarInteractionTimeoutRef.current);
    }

    toolbarInteractionTimeoutRef.current = window.setTimeout(() => {
      isToolbarInteractingRef.current = false;
      toolbarInteractionTimeoutRef.current = null;

      if (
        !editor?.isFocused &&
        !isMobilePopoverOpenRef.current &&
        mobileToolbarViewRef.current === 'main'
      ) {
        setIsMobileToolbarVisible(false);
      }
    }, MOBILE_TOOLBAR_INTERACTION_GRACE_MS);
  };

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentValue = sanitizeEventDescriptionHtml(editor.getHTML());
    const nextValue = sanitizeEventDescriptionHtml(value);

    if (currentValue !== nextValue) {
      editor.commands.setContent(nextValue, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!isMobile) {
      if (toolbarInteractionTimeoutRef.current !== null) {
        window.clearTimeout(toolbarInteractionTimeoutRef.current);
        toolbarInteractionTimeoutRef.current = null;
      }

      isToolbarInteractingRef.current = false;
      setIsMobileToolbarVisible(false);
      setIsMobilePopoverOpen(false);
      setMobileToolbarView('main');
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    if (isMobilePopoverOpen) {
      setIsMobileToolbarVisible(true);
      return;
    }

    if (
      !editor?.isFocused &&
      !isToolbarInteractingRef.current &&
      mobileToolbarView === 'main'
    ) {
      setIsMobileToolbarVisible(false);
    }
  }, [editor, isMobile, isMobilePopoverOpen, mobileToolbarView]);

  const syncLinkHrefFromEditor = () => {
    if (!editor) {
      return;
    }

    setLinkHref(editor.getAttributes('link').href ?? '');
  };

  const openLinkEditor = () => {
    if (!editor || disabled) {
      return;
    }

    syncLinkHrefFromEditor();

    if (isMobile) {
      setIsMobileToolbarVisible(true);
      setMobileToolbarView('link');
      return;
    }
  };

  const applyLink = () => {
    if (!editor) {
      return;
    }

    const normalizedHref = normalizeEventDescriptionLinkUrl(linkHref);

    if (!normalizedHref) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: normalizedHref })
        .run();
    }

    setLinkHref(normalizedHref);

    if (isMobile) {
      setMobileToolbarView('main');
    }
  };

  const removeLink = () => {
    if (!editor) {
      return;
    }

    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkHref('');

    if (isMobile) {
      setMobileToolbarView('main');
    }
  };

  const openCurrentLink = () => {
    const href = normalizeEventDescriptionLinkUrl(linkHref);

    if (!href || typeof window === 'undefined') {
      return;
    }

    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const mobileKeyboardInset = useMemo(() => {
    if (typeof window === 'undefined' || viewportHeight === 0) {
      return 0;
    }

    return Math.max(0, window.innerHeight - viewportHeight - offsetTop);
  }, [offsetTop, viewportHeight]);

  const mobileToolbarBottom =
    mobileKeyboardInset > 0
      ? mobileKeyboardInset + MOBILE_TOOLBAR_KEYBOARD_GAP
      : MOBILE_TOOLBAR_NAV_OFFSET;

  useEffect(() => {
    if (!editor || !isMobile || !isMobileToolbarVisible) {
      return;
    }

    const scrollContainer = document.getElementById('app-scroll-container');

    if (!(scrollContainer instanceof HTMLElement)) {
      return;
    }

    const ensureCursorVisible = () => {
      if (!editor.isFocused) {
        return;
      }

      const { from } = editor.state.selection;
      const cursorCoords = editor.view.coordsAtPos(from);
      const toolbarHeight =
        toolbarRef.current?.getBoundingClientRect().height ?? 0;
      const overlayTop =
        window.innerHeight - mobileToolbarBottom - toolbarHeight;
      const visibleBottom = overlayTop - MOBILE_CURSOR_BUFFER;

      if (cursorCoords.bottom > visibleBottom) {
        scrollContainer.scrollBy({
          top: cursorCoords.bottom - visibleBottom,
          behavior: 'smooth',
        });
      }
    };

    ensureCursorVisible();
    editor.on('selectionUpdate', ensureCursorVisible);

    return () => {
      editor.off('selectionUpdate', ensureCursorVisible);
    };
  }, [editor, isMobile, isMobileToolbarVisible, mobileToolbarBottom]);

  const handleSurfaceClick = () => {
    if (!editor || disabled) {
      return;
    }

    if (isMobile) {
      setIsMobileToolbarVisible(true);
    }

    editor.commands.focus();
  };

  const linkEditor = (
    <LinkEditor
      href={linkHref}
      onHrefChange={setLinkHref}
      onApply={applyLink}
      onRemove={removeLink}
      onOpenLink={openCurrentLink}
      canRemove={Boolean(editor?.getAttributes('link').href)}
    />
  );

  const desktopToolbar =
    editor && !isMobile ? (
      <div className="simple-editor__toolbar">
        <MainToolbar
          editor={editor}
          onOpenLink={openLinkEditor}
          linkEditor={linkEditor}
        />
      </div>
    ) : null;

  const mobileToolbar =
    editor &&
    isMobile &&
    isMobileToolbarVisible &&
    typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={toolbarRef}
            className={cn(
              'simple-editor__mobile-toolbar',
              mobileToolbarView === 'link' &&
                'simple-editor__mobile-toolbar--link'
            )}
            onPointerDownCapture={registerToolbarInteraction}
            style={{
              bottom: `${mobileToolbarBottom}px`,
            }}
          >
            {mobileToolbarView === 'main' ? (
              <MainToolbar
                editor={editor}
                isMobile
                onOpenLink={openLinkEditor}
                onOverlayOpenChange={setIsMobilePopoverOpen}
                onToolbarInteraction={registerToolbarInteraction}
              />
            ) : (
              <>
                <div className="simple-editor__toolbar-group">
                  <ToolbarButton
                    onClick={() => setMobileToolbarView('main')}
                    aria-label="Back to formatting"
                  >
                    <ArrowLeft size={18} weight="bold" />
                  </ToolbarButton>
                </div>
                <div className="simple-editor__toolbar-separator" />
                {linkEditor}
              </>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      className={cn(
        'simple-editor-shell',
        invalid && 'simple-editor-shell--invalid',
        disabled && 'simple-editor-shell--disabled'
      )}
    >
      {desktopToolbar}

      <div
        className="simple-editor-shell__surface"
        onClick={handleSurfaceClick}
      >
        <EditorContent editor={editor} role="presentation" />
      </div>

      {mobileToolbar}
    </div>
  );
}
